/**
 * 高山 一軒家（民泊）の設計データ。
 * 「高山_3D図面_2026.7.24時点.pdf」の俯瞰図（1ページ目＝1階、11ページ目＝2階）と各部屋のパースから起こした。
 *
 * 寸法の根拠: 俯瞰図でダイニングテーブル（8人掛け・約2.1m）と浴槽（約1.2m）を物差しにして 72px ≒ 1m とした。
 * 図面に無い所は一般的な住宅の寸法で補っている（天井高 2.45〜2.7m、階段13段、壁厚 12cm）。
 *
 * 座標: x 東西（図面の右が +）、z 南北（図面の下が +）、y 高さ。左上の外壁の角が原点。
 * 建物は西側（x 0〜6.6）が平屋の LDK、東側（x 6.6〜11.4）が2階建て。
 */

const F2 = 2.75; // 2階の床の高さ
const H1 = 2.45; // 東側1階の天井高
const H2 = 2.35; // 2階の天井高
const HL = 2.7; // LDK の天井高

const ext = (from, to, h, openings = []) => ({ from, to, h, openings, mat: 'plaster' });
const inner1 = (from, to, openings = [], mat = 'plaster') => ({ from, to, h: H1, openings, mat });
const inner2 = (from, to, openings = []) => ({ from, to, y: F2, h: H2, openings, mat: 'plaster', skirting: false });
const door = (a, w = 0.8, top = 2.05) => ({ a, w, top });
const win = (a, w, sill, top, fill = 'frosted') => ({ a, w, sill, top, fill });

export default {
  id: 'takayama',
  ambient: 1.4,
  sun: 0.7,

  rooms: [
    // ---- 1階 西側（平屋）----
    { x: [0, 6.6], z: [0, 6.1], floor: 'oak', ceil: 'woodCeiling', h: HL, roof: 'roof', ceilThick: 0.2 },
    { x: [4.7, 6.6], z: [6.1, 8.0], floor: 'tileDark', ceil: 'plaster', h: 2.4, roof: 'roof', ceilThick: 0.2 },
    { x: [4.5, 6.8], z: [8.0, 8.9], floor: 'tileGrey' },
    // ---- 1階 東側 ----
    { x: [6.6, 7.4], z: [0, 2.0], floor: 'cedar' },
    { x: [7.4, 9.6], z: [0, 2.0], floor: 'tileLight' },
    { x: [9.6, 11.4], z: [0, 2.0], floor: 'tileLight' },
    { x: [6.6, 7.4], z: [2.0, 6.6], floor: 'walnut' },
    { x: [7.4, 9.6], z: [2.0, 3.0], floor: 'walnut' },
    { x: [9.6, 11.4], z: [2.0, 3.0], floor: 'tileLight' },
    { x: [7.4, 11.4], z: [3.9, 6.6], floor: 'oak' },
    { x: [6.6, 10.1], z: [6.6, 8.6], floor: 'tileBeige' },
    { x: [10.1, 11.4], z: [6.6, 8.6], floor: 'walnut' },
    { x: [6.6, 11.4], z: [8.6, 10.5], floor: 'stone' },
    // ---- 2階の床（1階の天井を兼ねる。階段の吹き抜け x 7.4〜9.6 / z 3.0〜3.9 を除く）----
    { x: [6.6, 11.4], z: [0, 3.0], y: F2, thick: 0.3, floor: 'walnut', under: 'woodCeiling', ceil: 'plaster', h: H2, roof: 'roof', ceilThick: 0.2 },
    { x: [6.6, 7.4], z: [3.0, 3.9], y: F2, thick: 0.3, floor: 'walnut', under: 'woodCeiling', ceil: 'plaster', h: H2, roof: 'roof', ceilThick: 0.2 },
    { x: [9.6, 11.4], z: [3.0, 3.9], y: F2, thick: 0.3, floor: 'walnut', under: 'woodCeiling', ceil: 'plaster', h: H2, roof: 'roof', ceilThick: 0.2 },
    { x: [6.6, 11.4], z: [3.9, 10.5], y: F2, thick: 0.3, floor: 'walnut', under: 'woodCeiling', ceil: 'plaster', h: H2, roof: 'roof', ceilThick: 0.2 },
    // 階段の吹き抜けの上の天井
    { x: [7.4, 9.6], z: [3.0, 3.9], y: F2, ceil: 'plaster', h: H2, roof: 'roof', ceilThick: 0.2 },
    // 2階トイレの床（タイルを薄く重ねる）
    { x: [10.3, 11.4], z: [0, 2.0], y: F2 + 0.01, thick: 0.01, floor: 'tileLight' },
  ],

  walls: [
    // ---- 外壁: LDK（平屋）----
    ext([0, 0], [0, 6.1], 2.9, [win(0.4, 3.5, 0.85, 2.0, 'shoji'), win(4.3, 1.5, 0.85, 2.0, 'shoji')]),
    ext([0, 0], [6.6, 0], 2.9),
    ext([0, 6.1], [4.7, 6.1], 2.9, [win(1.0, 2.4, 0.5, 2.1, 'glass')]),
    // LDK と玄関の間（引き違いのガラス戸。片側は閉じた戸を家具で置く）
    { from: [4.7, 6.1], to: [6.6, 6.1], h: 2.9, mat: 'plaster', openings: [{ a: 0.25, w: 1.5, top: 2.1, frameColor: 0xe8e8e8 }] },
    // ---- 玄関 ----
    ext([4.7, 6.1], [4.7, 8.0], 2.9, [win(0.5, 1.0, 1.2, 1.9, 'lattice')]),
    ext([4.7, 8.0], [6.6, 8.0], 2.9, [door(0.5, 0.9)]),
    // ---- LDK と東側の境（2階まで通る壁）----
    ext([6.6, 0], [6.6, 10.5], F2 + H2 + 0.2, [
      door(4.4, 0.85),
      win(0.6, 1.4, F2 + 1.0, F2 + 1.9),
      win(4.6, 2.0, F2 + 1.0, F2 + 2.0, 'shoji'),
      win(8.2, 1.6, F2 + 1.0, F2 + 1.9),
    ]),
    // ---- 外壁: 東側（2階建て）----
    ext([6.6, 0], [11.4, 0], F2 + H2 + 0.2, [
      win(1.4, 1.4, 1.2, 1.95),
      win(3.6, 1.2, 1.2, 1.95),
      win(0.9, 0.8, F2 + 0.85, F2 + 2.2, 'shoji'),
      win(2.2, 1.4, F2 + 1.55, F2 + 2.15, 'shoji'),
      win(4.0, 0.8, F2 + 1.25, F2 + 1.95),
    ]),
    ext([11.4, 0], [11.4, 10.5], F2 + H2 + 0.2, [
      win(2.3, 0.5, 1.2, 1.8),
      win(4.4, 1.3, 1.1, 1.75),
      win(5.0, 0.9, F2 + 1.15, F2 + 1.95),
    ]),
    ext([6.6, 10.5], [11.4, 10.5], F2 + H2 + 0.2, [win(1.0, 2.6, F2 + 0.95, F2 + 1.75)]),

    // ---- 1階 内壁 ----
    inner1([6.6, 2.0], [11.4, 2.0], [door(0.15, 0.7, 2.0), door(1.7, 0.8)]),
    inner1([7.4, 0], [7.4, 2.0], [], 'cedar'),
    inner1([9.6, 0], [9.6, 2.0], [door(1.1, 0.7, 2.0)]),
    inner1([9.6, 2.0], [9.6, 3.0], [door(0.15, 0.7)]),
    inner1([7.4, 3.0], [9.6, 3.0]),
    inner1([7.4, 3.9], [11.4, 3.9]),
    inner1([9.6, 3.0], [9.6, 3.9]),
    inner1([7.4, 3.9], [7.4, 6.6], [door(1.7, 0.8)]),
    inner1([7.4, 6.6], [11.4, 6.6], [door(0.4, 0.8)]),
    inner1([10.1, 6.6], [10.1, 8.6], [door(0.6, 0.75)]),
    // 外気浴スペースとラウンジの間（ガラスの引き戸。片側は閉じた戸を家具で置く）
    { from: [6.6, 8.6], to: [11.4, 8.6], h: H1, mat: 'plaster', openings: [{ a: 0.4, w: 2.6, top: 2.2, frameColor: 0xe8e8e8 }] },

    // ---- 2階 内壁 ----
    inner2([6.6, 3.0], [10.3, 3.0]),
    inner2([10.3, 0], [10.3, 3.0], [door(2.15, 0.7)]),
    inner2([10.3, 2.0], [11.4, 2.0], [door(0.2, 0.7)]),
    inner2([6.6, 3.9], [10.3, 3.9]),
    inner2([10.3, 3.9], [10.3, 7.6], [door(2.9, 0.8)]),
    inner2([6.6, 7.6], [11.4, 7.6], [door(3.9, 0.8)]),
  ],

  stairs: [{ x0: 7.4, x1: 9.6, z0: 3.06, z1: 3.84, y0: 0, y1: F2, steps: 13, railZ: 3.12 }],

  furniture: [
    // ---- LDK: 畳の小上がりと掘りごたつ ----
    { type: 'platform', x: 1.8, z: 2.15, w: 2.9, d: 3.7, h: 0.35 },
    { type: 'kotatsu', x: 1.8, y: 0.35, z: 2.0, w: 1.6, d: 0.85, sku: 'zataku' },
    ...[1.25, 1.8, 2.35].flatMap((x) => [
      { type: 'zabuton', x, y: 0.35, z: 1.2, sku: 'zabuton' },
      { type: 'zabuton', x, y: 0.35, z: 2.8, sku: 'zabuton' },
    ]),
    { type: 'zabuton', x: 0.7, y: 0.35, z: 2.0, sku: 'zabuton' },
    { type: 'zabuton', x: 2.9, y: 0.35, z: 2.0, sku: 'zabuton' },
    { type: 'lantern', x: 1.8, y: HL, z: 2.0, drop: 0.7 },
    // 小上がりで見るテレビ。壁寄せスタンドに載せる
    { type: 'tvStand', x: 1.8, y: 0.35, z: 0.5, h: 0.75, sku: 'tvStand' },
    { type: 'tv', x: 1.8, y: 1.1, z: 0.52, w: 1.23, h: 0.71, sku: 'tv' },
    // 北側の濃い色のアクセント壁と額
    { type: 'box', x: 1.85, z: 0.075, w: 3.6, h: HL, d: 0.02, mat: 'felt' },
    { type: 'picture', x: 3.2, y: 1.5, z: 0.1, w: 0.45, h: 0.55, color: 0xe8e2d6 },
    { type: 'coveLight', x: 1.85, y: HL - 0.12, z: 0.13, len: 3.5 },
    { type: 'ac', x: 0.08, y: 2.2, z: 5.3, rot: 90, sku: 'acLdk' },
    // 押入れ（襖）
    { type: 'box', x: 5.15, z: 0.5, w: 2.9, h: 2.3, d: 1.0, mat: 'plaster' },
    { type: 'fusuma', x: 4.425, z: 1.02, w: 1.45, h: 2.3 },
    { type: 'fusuma', x: 5.875, z: 1.02, w: 1.45, h: 2.3 },
    // 柱と梁
    { type: 'post', x: 2.3, z: 4.8, h: HL },
    { type: 'post', x: 4.6, z: 4.8, h: HL },
    { type: 'beam', x: 3.3, z: 4.8, len: 6.6, y: HL - 0.16 },
    { type: 'beam', x: 3.3, z: 2.4, len: 6.6, y: HL - 0.16 },
    /*
     * リビング（竹プランの見積りどおり）。
     * 見積りで8人掛けダイニングテーブルをやめてL字ソファに変えたため、CG もそれに合わせている。
     * L字ソファは無印良品のユニットソファ（幅252cm）で、2人掛け2台とオットマンで形を作る。
     */
    { type: 'box', x: 5.3, z: 3.3, w: 1.91, h: 0.012, d: 1.91, mat: 'tatami', sku: 'rug' },
    // 西向きの1台と南向きの1台でL字を組む。角は南西
    { type: 'sofa', x: 4.25, z: 3.0, rot: 90, color: 0xcfc6b4, sku: 'sofa' },
    { type: 'sofa', x: 5.3, z: 4.55, rot: 180, color: 0xcfc6b4, sku: 'sofa' },
    { type: 'ottoman', x: 6.1, z: 2.9, color: 0xcfc6b4, sku: 'sofa' },
    { type: 'table', x: 5.4, z: 3.3, w: 1.3, d: 0.75, h: 0.37, runner: false, sku: 'lowTable' },
    { type: 'projector', x: 5.4, y: 0.37, z: 3.0, sku: 'projector' },
    { type: 'screen', x: 6.52, y: 0.95, z: 3.0, w: 2.21, h: 1.24, rot: -90, sku: 'screen' },
    ...[2.4, 3.0, 3.6].map((z) => ({ type: 'pendant', x: 5.3, y: HL, z, drop: 0.8, sku: 'pendant' })),
    { type: 'appliance', x: 6.35, z: 1.5, w: 0.26, h: 0.53, d: 0.26, color: 0xe9e7e2, sku: 'airPurifier' },
    { type: 'appliance', x: 3.55, z: 5.7, w: 0.29, h: 0.31, d: 0.29, color: 0xf2f0eb, sku: 'fan' },
    { type: 'appliance', x: 6.35, z: 5.3, w: 0.64, h: 0.48, d: 0.21, color: 0xf6f4ef, sku: 'gasHeater' },

    // ---- 玄関 ----
    { type: 'box', x: 4.99, z: 7.0, w: 1.6, h: 0.8, d: 0.45, mat: 'walnut', rot: 90 },
    { type: 'plant', x: 4.99, y: 0.8, z: 6.6, size: 0.45 },
    { type: 'glassDoor', x: 5.325, z: 6.1, w: 0.75, h: 2.1, panels: 1 },
    { type: 'bench', x: 6.3, z: 7.5, len: 1.0, d: 0.3, h: 0.44, rot: 90, sku: 'entryBench' },
    { type: 'mirror', x: 4.76, y: 0.3, z: 7.5, w: 0.27, h: 1.475, rot: 90, sku: 'fullMirror' },
    { type: 'appliance', x: 5.9, z: 6.45, w: 0.25, h: 0.28, d: 0.3, color: 0xf0efe9, sku: 'shoeDryer' },
    // 下駄箱の上にまとめる設備
    { type: 'appliance', x: 4.99, y: 0.8, z: 7.45, w: 0.17, h: 0.21, d: 0.17, color: 0x2f3237, panel: false, sku: 'wifi' },
    { type: 'appliance', x: 4.99, y: 0.8, z: 6.9, w: 0.35, h: 0.25, d: 0.28, color: 0x4a4d52, sku: 'safe' },

    // ---- サウナ ----
    { type: 'heater', x: 6.95, z: 0.35 },
    { type: 'bench', x: 7.0, z: 1.55, len: 0.7, d: 0.5 },
    { type: 'box', x: 6.68, z: 1.0, w: 0.03, h: H1, d: 1.9, mat: 'cedar' },
    { type: 'box', x: 7.0, y: 0.45, z: 1.55, w: 0.6, h: 0.02, d: 0.4, mat: 'linen', sku: 'saunaMat' },

    // ---- 洗面脱衣 ----
    { type: 'basin', x: 8.0, z: 0.35, w: 0.9, d: 0.5 },
    { type: 'mirror', x: 8.0, y: 1.3, z: 0.08, w: 0.7, h: 0.7 },
    { type: 'washer', x: 9.2, z: 0.4, sku: 'washer' },
    { type: 'shelf', x: 8.85, y: 1.3, z: 0.2, w: 0.5, d: 0.25 },
    { type: 'shelf', x: 8.85, y: 1.6, z: 0.2, w: 0.5, d: 0.25 },
    { type: 'appliance', x: 8.0, y: 0.72, z: 0.35, w: 0.22, h: 0.18, d: 0.14, color: 0x2e3135, panel: false, sku: 'dryer' },
    { type: 'appliance', x: 7.65, z: 1.65, w: 0.35, h: 0.57, d: 0.26, color: 0xeceae5, sku: 'dehumidifier' },

    // ---- 浴室 ----
    { type: 'bathtub', x: 10.5, z: 0.45, w: 1.6, d: 0.75, h: 0.55 },
    { type: 'shelf', x: 9.9, y: 1.4, z: 1.2, w: 0.3, d: 0.25 },
    { type: 'shelf', x: 9.9, y: 1.7, z: 1.2, w: 0.3, d: 0.25 },

    // ---- 1階トイレ ----
    { type: 'toilet', x: 11.05, z: 2.55, rot: -90 },
    { type: 'basin', x: 10.4, z: 2.25, w: 0.45, d: 0.35 },
    { type: 'mirror', x: 10.4, y: 1.4, z: 2.08, w: 0.4, h: 0.5 },
    { type: 'pendant', x: 10.5, y: H1, z: 2.5, drop: 0.6 },

    // ---- キッチン ----
    { type: 'kitchen', x: 11.34, z: 5.0, len: 2.4, rot: -90 },
    { type: 'counter', x: 8.4, z: 4.7, len: 1.4, d: 0.7, rot: 90, sku: 'kitchenCounter' },
    ...[4.1, 4.7, 5.3].map((z) => ({ type: 'stool', x: 8.05, z, sku: 'stool' })),
    { type: 'fridge', x: 10.95, z: 4.25, w: 0.75, h: 1.83, d: 0.7, sku: 'fridge' },
    // 調理家電はキッチンの天板（高さ0.85m）に並べる
    { type: 'appliance', x: 11.0, y: 0.89, z: 6.1, w: 0.5, h: 0.36, d: 0.4, color: 0x2b2e32, sku: 'microwave', rot: -90 },
    { type: 'appliance', x: 11.0, y: 0.89, z: 5.6, w: 0.28, h: 0.24, d: 0.36, color: 0xf2efe9, sku: 'riceCooker', rot: -90 },
    { type: 'appliance', x: 11.0, y: 0.89, z: 5.2, w: 0.35, h: 0.25, d: 0.32, color: 0x3a3d41, sku: 'toaster', rot: -90 },
    { type: 'appliance', x: 11.0, y: 0.89, z: 4.85, w: 0.22, h: 0.26, d: 0.16, color: 0xdedbd5, panel: false, sku: 'kettle', rot: -90 },
    { type: 'box', x: 7.47, z: 5.2, w: 0.02, h: H1, d: 2.6, mat: 'felt' },
    { type: 'picture', x: 7.5, y: 1.5, z: 4.6, w: 0.7, h: 0.5, color: 0x2a2a2a, rot: 90 },
    { type: 'spotRail', x: 8.4, y: H1, z: 4.7, len: 1.2 },

    // ---- 外気浴スペース（タイル）----
    { type: 'plant', x: 7.1, z: 7.1, size: 1.3 },
    { type: 'glassDoor', x: 7.65, z: 8.6, w: 1.3, h: 2.2, panels: 1 },

    // ---- 備品庫 ----
    { type: 'steelRack', x: 10.75, z: 6.95, w: 0.9, d: 0.45, h: 1.8, sku: 'steelRack' },
    { type: 'steelRack', x: 10.75, z: 7.5, w: 0.9, d: 0.45, h: 1.8, sku: 'steelRack' },

    // ---- ラウンジ（水風呂ととのいスペース）----
    { type: 'bathtub', x: 10.1, z: 9.3, w: 1.6, d: 0.75, h: 0.55 },
    { type: 'chaise', x: 7.7, z: 9.4, rot: 180, sku: 'saunaChair' },
    { type: 'chaise', x: 7.7, z: 10.0, rot: 180, sku: 'saunaChair' },
    { type: 'sideTable', x: 9.0, z: 8.9 },
    { type: 'slatPanel', x: 9.0, y: 0.9, z: 10.4, w: 2.6, h: 1.5 },
    { type: 'shelf', x: 10.9, y: 1.5, z: 10.3, w: 0.4, d: 0.25 },
    { type: 'shelf', x: 10.9, y: 1.8, z: 10.3, w: 0.4, d: 0.25 },
    { type: 'coveLight', x: 9.0, y: H1 - 0.06, z: 10.36, len: 4.6 },

    // ---- 2階 寝室1（北・シングル2台）----
    { type: 'bed', x: 7.5, y: F2, z: 1.3, w: 1.0, d: 2.0, sku: 'bedSingle' },
    { type: 'bed', x: 8.9, y: F2, z: 1.3, w: 1.0, d: 2.0, sku: 'bedSingle' },
    { type: 'box', x: 8.2, y: F2, z: 0.1, w: 3.0, h: 1.0, d: 0.06, mat: 'walnut' },
    { type: 'coveLight', x: 8.2, y: F2 + 1.0, z: 0.14, len: 3.0 },
    { type: 'sideTable', x: 8.2, y: F2, z: 0.45, sku: 'nightTable' },
    { type: 'lamp', x: 8.2, y: F2 + 0.5, z: 0.45, sku: 'bedLamp' },
    { type: 'dresser', x: 9.95, y: F2, z: 2.6, w: 0.8, d: 0.4, rot: 180, sku: 'dresser' },
    { type: 'appliance', x: 6.95, y: F2, z: 1.9, w: 0.24, h: 0.32, d: 0.24, color: 0xf2f0ea, sku: 'humidifier' },
    { type: 'plant', x: 6.95, y: F2, z: 0.45, size: 0.5 },
    { type: 'ac', x: 6.7, y: F2 + 2.0, z: 0.8, rot: 90, sku: 'acBed' },
    { type: 'pendant', x: 8.2, y: F2 + H2, z: 1.6, drop: 0.35, sku: 'ceilingLight' },

    // ---- 2階トイレ ----
    { type: 'toilet', x: 10.85, y: F2, z: 0.5 },
    { type: 'basin', x: 11.15, y: F2, z: 1.5, w: 0.45, d: 0.35, rot: -90 },
    { type: 'pendant', x: 10.85, y: F2 + H2, z: 1.0, drop: 0.7 },

    // ---- 2階 寝室2（中央・ダブル2台）----
    { type: 'bed', x: 7.6, y: F2, z: 5.1, w: 1.4, d: 2.0, sku: 'bedDouble' },
    { type: 'bed', x: 9.15, y: F2, z: 5.1, w: 1.4, d: 2.0, sku: 'bedDouble' },
    { type: 'box', x: 8.4, y: F2, z: 3.99, w: 3.4, h: 1.1, d: 0.06, mat: 'walnut' },
    { type: 'coveLight', x: 8.4, y: F2 + 1.1, z: 4.03, len: 3.4 },
    { type: 'blind', x: 8.4, y: F2 + 1.6, z: 3.97, w: 3.0, h: 0.9 },
    { type: 'sideTable', x: 8.4, y: F2, z: 4.5, sku: 'nightTable' },
    { type: 'lamp', x: 8.4, y: F2 + 0.5, z: 4.5, sku: 'bedLamp' },
    { type: 'appliance', x: 10.5, y: F2, z: 4.4, w: 0.24, h: 0.32, d: 0.24, color: 0xf2f0ea, sku: 'humidifier' },
    { type: 'ac', x: 10.2, y: F2 + 2.0, z: 6.5, rot: -90, sku: 'acBed' },
    { type: 'pendant', x: 8.4, y: F2 + H2, z: 5.4, drop: 0.35, sku: 'ceilingLight' },

    // ---- 2階 寝室3（南・ダブル2台）----
    { type: 'bed', x: 7.9, y: F2, z: 9.3, w: 1.2, d: 2.0, rot: 180, sku: 'bedDouble' },
    { type: 'bed', x: 9.3, y: F2, z: 9.3, w: 1.2, d: 2.0, rot: 180, sku: 'bedDouble' },
    { type: 'box', x: 8.6, y: F2, z: 10.4, w: 3.2, h: 1.0, d: 0.06, mat: 'walnut' },
    { type: 'coveLight', x: 8.6, y: F2 + 1.0, z: 10.36, len: 3.2 },
    { type: 'ottoman', x: 7.0, y: F2, z: 8.2 },
    { type: 'sideTable', x: 10.6, y: F2, z: 9.8, sku: 'nightTable' },
    { type: 'lamp', x: 10.6, y: F2 + 0.5, z: 9.8, sku: 'bedLamp' },
    { type: 'appliance', x: 7.0, y: F2, z: 10.1, w: 0.24, h: 0.32, d: 0.24, color: 0xf2f0ea, sku: 'humidifier' },
    { type: 'ac', x: 11.3, y: F2 + 2.0, z: 8.4, rot: -90, sku: 'acBed' },
    { type: 'pendant', x: 8.6, y: F2 + H2, z: 9.2, drop: 0.35, sku: 'ceilingLight' },
  ],

  lights: [
    { x: 3.3, y: 2.0, z: 3.0, intensity: 7, distance: 9 },
    { x: 1.8, y: 1.9, z: 2.0, intensity: 4, distance: 6 },
    { x: 5.6, y: 1.9, z: 7.0, intensity: 2.5, distance: 4 },
    { x: 7.0, y: 1.9, z: 4.0, intensity: 2.5, distance: 5 },
    { x: 8.5, y: 1.9, z: 1.0, intensity: 2.5, distance: 4 },
    { x: 9.4, y: 1.9, z: 5.2, intensity: 4, distance: 5 },
    { x: 8.4, y: 1.9, z: 7.6, intensity: 3, distance: 5 },
    { x: 9.0, y: 1.8, z: 9.5, intensity: 5, distance: 5 },
    { x: 8.4, y: F2 + 1.8, z: 1.5, intensity: 3.5, distance: 5 },
    { x: 8.4, y: F2 + 1.8, z: 5.7, intensity: 4, distance: 5 },
    { x: 9.0, y: F2 + 1.8, z: 9.0, intensity: 4, distance: 5 },
    { x: 10.8, y: F2 + 1.8, z: 4.5, intensity: 2.5, distance: 5 },
  ],
};
